import { Box, Button, Grid } from "@mui/material";
import { set } from "lodash";
import { useState } from "react";
import useAsyncEffect from "use-async-effect";
import { useQueryClient } from "@tanstack/react-query";
import { HistoryProps } from "../..";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import ComponentLoader from "../../../../../../components/ComponentLoader";
import { NotificationVariant } from "../../../../../../components/dashboard/NotificationsProvider";
import NotesInput from "../../../../../../components/form/NotesInput";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import useNotifications from "../../../../../../hooks/useNotifications";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { createReference } from "../../../../../../utils/DataTransformationUtils";
import { FHIRURL } from "../../../../../../utils/FHIRURL";
import {
  QuestionnaireItem,
  QuestionnaireItemTypes,
  createFlattendResponses,
  createQuestionnaireItemPath,
  createResponseItem,
  enableQuestion,
} from "../../../components/QuestionnaireItemTypes";
import { emptyHistoryItems } from "../HistoryHelpers";
import { QueryKeys } from "../../../../../../constants/queryKeys";
import { useDictationCheck } from "../../../../../../hooks/useDictationCheck";

const ENTITY_TYPE = "questionnaire";
const OBGYN_QUESTIONNAIRE_CODE = "63a20c7e-fee9-4634-b092-731faed6ad2d";

export default function GynaecologicalHistoryForm({
  patientId,
  patientReference: _patientReference,
  disabled,
  setSaved,
}: HistoryProps) {
  const {
    currentPatient,
    encounterReference,
    patientReference,
    setEncounterToInProgress,
    refreshPatientHistory,
  } = useMedicalEncounter();

  const practitionerReference = usePractitioner();
  const { isDictationActive } = useDictationCheck();

  const [loading, setLoading] = useState(false);
  const [questionnaire, setQuestionnaire] = useState<any>();
  const [flattenedAnswers, setFlattenedAnswers] = useState({});
  const [questionnaireResponse, setQuestionnaireResponse] = useState<any>({
    ...emptyHistoryItems.gynaecological(patientReference),
  });

  const { setNotification } = useNotifications();

  const queryClient = useQueryClient();

  useAsyncEffect(async () => {
    try {
      setLoading(true);

      const _response = await medicalsApi.findQuestionnaireResponse(
        OBGYN_QUESTIONNAIRE_CODE,
        {
          subject: patientReference?.identifier ?? patientId,
          e: encounterReference?.identifier,
          "exclude-status": "entered-in-error",
        },
      );

      if (!Array.isArray(_response) || _response.length > 1) {
        throw new Error("Not array or array length more than 1");
      }

      let response;
      if (_response.length === 1) {
        response = _response[0];
      }

      if (response) {
        setQuestionnaireResponse(response);
        setFlattenedAnswers(createFlattendResponses(response.item || []));
      }

      let _questionnaire: any;
      if (response?.questionnaire) {
        const fhirUrl = new FHIRURL(response.questionnaire);
        _questionnaire = await medicalsApi.findEntities(
          {
            id: fhirUrl.id,
            version: fhirUrl.searchParams.get("version"),
            code: fhirUrl.searchParams.get("code"),
          },
          "questionnaire",
        );
      } else {
        _questionnaire = await medicalsApi.findEntities(
          { code: OBGYN_QUESTIONNAIRE_CODE, status: "active" },
          ENTITY_TYPE,
        );
      }

      if (!_questionnaire || _questionnaire.length === 0) {
        throw new Error(
          "Gynaecological history questionnaire is either more than or less than 1",
        );
      }

      setQuestionnaire(_questionnaire[0]);
    } catch (error) {
      console.error(error);
      setNotification({
        message:
          "Unable to retrieve Gynaecological history questionnaire. Please try again later.",
        variant: NotificationVariant.error,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const onItemChange = (item: QuestionnaireItem, value) => {
    const indices = item.linkId.split(".");
    const path = createQuestionnaireItemPath(indices);
    const _questionnaireResponse = { ...questionnaireResponse };
    set(_questionnaireResponse, path, createResponseItem(item, value));
    setQuestionnaireResponse(_questionnaireResponse);

    const flattened = createFlattendResponses(
      _questionnaireResponse.item || [],
    );

    setFlattenedAnswers(flattened);
    setSaved(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...questionnaireResponse,
        questionnaire: new FHIRURL(`Questionnaire/${questionnaire.id}`, {
          version: questionnaire.version,
          code: questionnaire.code[0]?.code,
        }).url,
        authored: new Date(),
        author: practitionerReference,
        source: practitionerReference,
        subject: patientReference ?? createReference("Patient", patientId),
      };

      if (encounterReference) {
        data.encounter = encounterReference;
      }

      if (!data.meta) {
        // In this case, create a new questionnaire response
        setQuestionnaireResponse(
          await medicalsApi.createEntity(
            { ...data, status: "completed" },
            "questionnaire-response",
          ),
        );
      } else {
        // Update existing questionnaire
        setQuestionnaireResponse(
          await medicalsApi.updateEntity(
            data.id,
            { ...data, status: "amended" },
            "questionnaire-response",
          ),
        );
      }

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.OBGYN_QUESTIONNAIRE_HISTORY],
      });

      // can be changed to await
      setEncounterToInProgress();

      setSaved(true);
      refreshPatientHistory();
      setNotification({
        variant: NotificationVariant.success,
        message: "Successfully saved the patient's gynaecological history.",
      });
    } catch (error) {
      console.error(error);
      setNotification({
        variant: NotificationVariant.error,
        message:
          "An error occurred while saving patient's gynaecological history. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {questionnaire && (
        <Grid container spacing={3} xs={12}>
          {questionnaire?.item?.map((item: QuestionnaireItem, i) => {
            const QuestionnaireComponent = item.type ? (
              QuestionnaireItemTypes[item.type]
            ) : (
              <></>
            );

            return (
              <>
                {enableQuestion(item, flattenedAnswers, onItemChange) && (
                  <Grid item container xs={12}>
                    <QuestionnaireComponent
                      key={`question-${i}`}
                      item={item}
                      onChange={onItemChange}
                      value={flattenedAnswers[item?.linkId]}
                      answers={flattenedAnswers}
                      disabled={disabled}
                      formula={item.formula}
                      sex={currentPatient.gender}
                    />
                  </Grid>
                )}
              </>
            );
          })}
        </Grid>
      )}
      <NotesInput
        disabled={disabled}
        value={questionnaireResponse?.note || []}
        label="Comments/Notes"
        onChange={(note) => {
          setQuestionnaireResponse({ ...questionnaireResponse, note });
          setSaved(false);
        }}
        placeholderText="Enter comments/notes about the patient's gynaecological history"
      />
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={disabled || loading || isDictationActive}
          sx={{
            mt: 2,
            mr: 3,
          }}
        >
          Save
        </Button>
      </Box>
      <ComponentLoader loading={loading} />
    </form>
  );
}
